import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Compact "medical flags" safety strip driven by active medical-history warnings.
 *
 * - Renders nothing if there are no active flags (keeps the profile clean).
 * - Each flag is a pill chip; hover/click shows the source conditions.
 * - Auto-refreshes when the parent bumps `refreshKey`.
 */
export default function MedicalFlagsStrip({ patientId, refreshKey }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}/medical-flags`);
      setFlags(data.flags || []);
    } catch {
      setFlags([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading || flags.length === 0) return null;

  return (
    <div
      data-testid="medical-flags-strip"
      className="mb-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white p-3 flex items-start gap-3"
    >
      <div className="w-8 h-8 rounded-full bg-amber-100 grid place-items-center text-amber-700 shrink-0">
        <ShieldAlert className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
            Medical safety flags
          </div>
          <span className="text-[10px] text-amber-700/70">· {flags.length} to review before treatment</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {flags.map((f, i) => (
            <Popover key={i}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid={`medical-flag-chip-${i}`}
                  className="group inline-flex items-center gap-1.5 max-w-full text-xs px-2.5 py-1 rounded-full border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 transition"
                >
                  <AlertTriangle className="w-3 h-3 shrink-0 text-amber-600" />
                  <span className="truncate">{f.text}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-3 text-xs" data-testid={`medical-flag-source-${i}`}>
                <div className="font-medium text-ink mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Clinical alert
                </div>
                <div className="text-ink/80 mb-2">{f.text}</div>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">
                  Triggered by
                </div>
                <div className="flex flex-wrap gap-1">
                  {f.conditions.map((c) => (
                    <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-linen text-ink">
                      {c}
                    </span>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          ))}
        </div>
      </div>
    </div>
  );
}
